import { useMemo, useState, type FormEvent } from "react";
import { Link as RouterLink } from "@tanstack/react-router";
import { useQueryClient } from "@tanstack/react-query";
import { Layers, Link2, Loader2 } from "lucide-react";
import { detectStoreHint } from "@affiliate/stores";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { STORE_CHIPS } from "@/lib/affiliate-ui";
import { callErrorMessage, convertLink, type ConversionResult } from "@/lib/hooks/useConvertLink";
import { ConversionResultCard } from "./ConversionResultCard";
import { Notice } from "./Notice";
import { ShortenToggle } from "./ShortenToggle";
import { StoreChips } from "./StoreChips";

/** Estado e envio da geracao de UM link pela edge convert-link. */
function useGenerateLink() {
  const queryClient = useQueryClient();
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState<ConversionResult | null>(null);
  const [callError, setCallError] = useState<string | null>(null);

  const generate = async (url: string, shorten: boolean): Promise<void> => {
    setResult(null);
    setCallError(null);
    if (!url) {
      setCallError("Cole o link do produto para começar.");
      return;
    }
    setLoading(true);
    try {
      const converted = await convertLink(url, shorten);
      setResult(converted);
      if (converted.status === "converted") {
        await queryClient.invalidateQueries({ queryKey: ["links"] });
        await queryClient.invalidateQueries({ queryKey: ["link-history"] });
      }
    } catch (error) {
      setCallError(callErrorMessage(error));
    } finally {
      setLoading(false);
    }
  };

  return { loading, result, callError, generate };
}

/** Gerar link: detecta a loja ao colar e converte de verdade pela edge convert-link. */
export function GenerateLinkPanel(): JSX.Element {
  const [url, setUrl] = useState("");
  const [shorten, setShorten] = useState(true);
  const { loading, result, callError, generate } = useGenerateLink();
  const detected = useMemo(() => detectStoreHint(url.trim()), [url]);
  const detectedLabel = STORE_CHIPS.find((chip) => chip.code === detected)?.label ?? null;

  const handleSubmit = (event: FormEvent<HTMLFormElement>): void => {
    event.preventDefault();
    void generate(url.trim(), shorten);
  };

  return (
    <Card variant="raised" className="overflow-hidden">
      <GenerateLinkHeader />
      <CardContent className="space-y-4 pt-5">
        <StoreChips activeCode={detected} />
        <form className="space-y-3" onSubmit={handleSubmit}>
          <div className="space-y-2">
            <Label htmlFor="generate-link-url" variant="field">Link do produto</Label>
            <Input id="generate-link-url" type="url" inputMode="url" value={url} onChange={(event) => setUrl(event.target.value)}
              placeholder="https://shopee.com.br/... ou https://amzn.to/..." autoComplete="off" />
            <p className="text-xs text-muted-foreground" aria-live="polite">
              {detectedLabel ? `Loja detectada: ${detectedLabel}.` : "Aceita links completos e encurtados (amzn.to, s.shopee.com.br, meli.la)."}
            </p>
          </div>
          <ShortenToggle checked={shorten} onChange={setShorten} />
          <Button type="submit" className="w-full sm:w-auto" disabled={loading}>
            {loading ? <Loader2 size={15} className="animate-spin" /> : <Link2 size={15} />}
            {loading ? "Gerando..." : "Gerar link"}
          </Button>
        </form>
        {callError && <Notice tone="danger">{callError}</Notice>}
        {result && <ConversionResultCard result={result} />}
      </CardContent>
    </Card>
  );
}

function GenerateLinkHeader(): JSX.Element {
  return (
    <CardHeader className="border-b border-border bg-[hsl(var(--surface-raised)/0.45)]">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="space-y-1">
          <CardTitle>Gerar link de afiliado</CardTitle>
          <CardDescription>
            Cole o link do produto. A loja é detectada na hora e o link oficial sai com a sua credencial.
          </CardDescription>
        </div>
        <Button asChild size="sm" variant="outline">
          <RouterLink to="/app/links/massa">
            <Layers size={14} />
            Em massa
          </RouterLink>
        </Button>
      </div>
    </CardHeader>
  );
}
